import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/auth/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { formatPrice, calculateWalletBonus, WalletBonusTier } from '../../utils/bookingUtils';
import TopUpModal from './TopUpModal';

interface WalletTransaction {
  id: string;
  type: 'topup' | 'bonus' | 'payment' | 'refund' | 'adjustment';
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
  stripe_payment_intent_id?: string;
  reference_id?: string;
}

interface Receipt {
  id: string;
  reference_id: string;
}

const WalletCard: React.FC = () => {
  const { userProfile, refreshUserProfile } = useAuth();
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [bonusTiers, setBonusTiers] = useState<WalletBonusTier[]>([]);
  const [topupReceipts, setTopupReceipts] = useState<Map<string, Receipt>>(new Map());
  const [isTopUpModalOpen, setIsTopUpModalOpen] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  const [showAllTransactions, setShowAllTransactions] = useState(false);

  const walletBalance = userProfile?.wallet_balance || 0;

  // Fetch transactions and bonus tiers
  useEffect(() => {
    const fetchData = async () => {
      if (!userProfile?.id) return;

      setIsLoadingTransactions(true);

      try {
        // Fetch recent transactions (table may not exist yet)
        const { data: txData, error: txError } = await supabase
          .from('wallet_transactions')
          .select('*')
          .eq('user_id', userProfile.id)
          .order('created_at', { ascending: false })
          .limit(showAllTransactions ? 50 : 5);

        if (!txError && txData) {
          setTransactions(txData);

          // Fetch receipts for wallet top-ups (linked via wallet_topups table)
          // First get wallet_topups to map stripe_payment_intent_id -> topup_id
          const topupPaymentIntentIds = txData
            .filter(tx => tx.type === 'topup' && tx.stripe_payment_intent_id)
            .map(tx => tx.stripe_payment_intent_id);

          if (topupPaymentIntentIds.length > 0) {
            const { data: topups } = await supabase
              .from('wallet_topups')
              .select('id, stripe_payment_intent_id')
              .in('stripe_payment_intent_id', topupPaymentIntentIds);

            if (topups && topups.length > 0) {
              const topupIds = topups.map(t => t.id);
              const { data: receipts } = await supabase
                .from('receipts')
                .select('id, reference_id')
                .eq('receipt_type', 'wallet_topup')
                .in('reference_id', topupIds);

              if (receipts) {
                // Create map: stripe_payment_intent_id -> receipt
                const receiptMap = new Map<string, Receipt>();
                receipts.forEach(r => {
                  const topup = topups.find(t => t.id === r.reference_id);
                  if (topup) {
                    receiptMap.set(topup.stripe_payment_intent_id, r as Receipt);
                  }
                });
                setTopupReceipts(receiptMap);
              }
            }
          }
        }
      } catch (err) {
        // Table may not exist yet
      }

      try {
        // Fetch bonus tiers (table may not exist yet)
        const { data: tierData, error: tierError } = await supabase
          .from('wallet_bonus_tiers')
          .select('*')
          .eq('is_active', true)
          .order('min_amount', { ascending: true });

        if (!tierError && tierData) {
          setBonusTiers(tierData);
        } else {
          // Use default tiers if table doesn't exist
          setBonusTiers([
            { min_amount: 500, bonus_percent: 20, bonus_amount: 100 },
            { min_amount: 1000, bonus_percent: 30, bonus_amount: 300 },
            { min_amount: 2000, bonus_percent: 50, bonus_amount: 1000 },
            { min_amount: 3000, bonus_percent: 50, bonus_amount: 1500 },
            { min_amount: 4000, bonus_percent: 63, bonus_amount: 2500 },
            { min_amount: 5000, bonus_percent: 60, bonus_amount: 3000 },
          ]);
        }
      } catch (err) {
        // Table may not exist yet - use default tiers
        setBonusTiers([
          { min_amount: 500, bonus_percent: 5 },
          { min_amount: 1000, bonus_percent: 10 },
          { min_amount: 2000, bonus_percent: 15 },
        ]);
      }

      setIsLoadingTransactions(false);
    };

    fetchData();
  }, [userProfile?.id, showAllTransactions]);

  const handleTopUpSuccess = async () => {
    await refreshUserProfile();
    // Refresh transactions
    if (userProfile?.id) {
      const { data } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('user_id', userProfile.id)
        .order('created_at', { ascending: false })
        .limit(showAllTransactions ? 50 : 5);
      if (data) setTransactions(data);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'topup':
        return (
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </div>
        );
      case 'bonus':
        return (
          <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
        );
      case 'payment':
        return (
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
        );
      case 'refund':
        return (
          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Wallet Balance Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-sm mb-1">Wallet Balance</p>
            <p className="text-3xl font-bold">${formatPrice(walletBalance)}</p>
          </div>
          <div className="bg-white/20 rounded-full p-3">
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
        </div>
        <button
          onClick={() => setIsTopUpModalOpen(true)}
          className="mt-4 w-full bg-white text-blue-600 py-2 px-4 rounded-lg font-medium hover:bg-blue-50 transition-colors"
        >
          Add Funds
        </button>
      </div>

      {/* Bonus Tiers Info */}
      {bonusTiers.length > 0 && (
        <div className="p-4 bg-yellow-50 border-b border-yellow-100">
          <p className="text-sm font-medium text-yellow-800 mb-2">Bonus Tiers</p>
          <div className="flex flex-wrap gap-2">
            {bonusTiers.map((tier) => (
              <span
                key={tier.min_amount}
                className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs"
              >
                ${tier.min_amount.toLocaleString()} → +${tier.bonus_amount != null ? tier.bonus_amount.toLocaleString() : Math.round(tier.min_amount * tier.bonus_percent / 100).toLocaleString()}
              </span>
            ))}
          </div>
          <p className="text-xs text-yellow-700 mt-2">
            Wallet top-up balance is non-refundable and cannot be redeemed for cash.
          </p>
        </div>
      )}

      {/* Recent Transactions */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900">Recent Activity</h3>
          {transactions.length > 0 && (
            <button
              onClick={() => setShowAllTransactions(!showAllTransactions)}
              className="text-sm text-blue-600 hover:underline"
            >
              {showAllTransactions ? 'Show Less' : 'View All'}
            </button>
          )}
        </div>

        {isLoadingTransactions ? (
          <div className="text-center py-4">
            <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4">
            No transactions yet. Add funds to get started!
          </p>
        ) : (
          <div className="space-y-3">
            {transactions.map((tx) => (
              <div key={tx.id} className="flex items-center gap-3">
                {getTransactionIcon(tx.type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{tx.description}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                    {tx.type === 'topup' && tx.stripe_payment_intent_id && topupReceipts.has(tx.stripe_payment_intent_id) && (
                      <Link
                        to={`/receipt/${topupReceipts.get(tx.stripe_payment_intent_id)?.id}`}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Receipt
                      </Link>
                    )}
                  </div>
                </div>
                <span
                  className={`font-medium ${
                    tx.amount >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {tx.amount >= 0 ? '+' : ''}${formatPrice(Math.abs(tx.amount))}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top Up Modal */}
      <TopUpModal
        isOpen={isTopUpModalOpen}
        onClose={() => setIsTopUpModalOpen(false)}
        onSuccess={handleTopUpSuccess}
        bonusTiers={bonusTiers}
      />
    </div>
  );
};

export default WalletCard;
